import { useEffect, useRef, useState } from 'react';
import { Mail } from 'lucide-react';
import { useActiveConversation } from '../../../chat/ActiveConversationContext';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import type { EmailCampaignPreviewCardData, EmailReviewMode } from '../../../../lib/email-review';
import { emailViewDocument, emailViewFileName, readOnlyViewDocument, reviewNonce, reviewedViewDocument, workspaceForSite } from '../../../../lib/email-review';
import { fetchEmailCampaignPreview, useEmailCampaignInlinePreview } from '../../../../lib/email-review-api';
import { openEmailReview, type SendEmailComments } from '../../../../lib/email-review-store';
import { openDocViewer } from '../../../../lib/doc-viewer-presenter';
import { useEmailPreviewWidgetEnabled } from '../../../../lib/email-preview-widget-flag';
import { useChatOverlayOpen } from '../../../../lib/use-chat-overlay';
import { useSitePaneFits } from '../../../../lib/use-site-pane';
import { useWorkspaces } from '../../../../lib/workspaces';
import type { SentReviewComment } from '../../../../lib/site-review';
import { Button, Card, Typography } from '../../ui';
import { EmailCampaignInlinePreview } from './EmailCampaignInlinePreview';
import { useReviewedViewLabels } from './EmailReviewMessage';
import styles from './EmailCampaignPreviewCard.module.css';

export function EmailCampaignPreviewCard({
  card,
  conversationId,
  cardKey,
  latest = true,
  reviewMode = 'comment',
  canSend = true,
  sendComments,
  pastComments,
}: {
  card: EmailCampaignPreviewCardData;
  conversationId?: string;
  cardKey?: string;
  latest?: boolean;
  reviewMode?: EmailReviewMode;
  canSend?: boolean;
  sendComments?: SendEmailComments;
  pastComments?: readonly SentReviewComment[];
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const workspaces = useWorkspaces();
  const workspaceId = workspaces.status === 'ready' ? workspaceForSite(workspaces.workspaces, card.metaSiteId) : null;
  const paneFits = useSitePaneFits();
  const chatOpen = useChatOverlayOpen();
  const { activeConversationId } = useActiveConversation();
  const beside = paneFits && chatOpen && conversationId != null && conversationId === activeConversationId;
  const enabled = useEmailPreviewWidgetEnabled();
  const reviewable = reviewMode !== null;
  const inlineKey = cardKey ?? conversationId ?? '';
  const loaded = useEmailCampaignInlinePreview(
    { workspaceId: workspaceId ?? '', metaSiteId: card.metaSiteId, campaignId: card.campaignId },
    inlineKey,
    false,
  );
  const subject = card.subject || loaded.data?.subject || '';
  const reviewedLabels = useReviewedViewLabels();
  const [viewing, setViewing] = useState<'idle' | 'loading' | 'unavailable'>('idle');
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  if (enabled !== true) return null;

  const view = async () => {
    if (!workspaceId) return;
    logPageClickBi({ page_name: 'chat', button_name: 'email_campaign_preview_view' });
    setViewing('loading');
    try {
      const preview = await fetchEmailCampaignPreview(workspaceId, card.metaSiteId, card.campaignId);
      const title = preview.subject || card.subject;
      openDocViewer({
        file: {
          name: emailViewFileName(title),
          isApp: false,
          content: pastComments?.length
            ? reviewedViewDocument(preview.html, pastComments, reviewedLabels(pastComments), reviewNonce())
            : emailViewDocument(preview.html),
        },
        kind: 'html',
        pageName: 'document_viewer',
        besideChat: beside,
        title: title || undefined,
        localOnly: true,
        ...(pastComments?.length ? {} : { viewText: readOnlyViewDocument(preview.html) }),
      });
      if (mounted.current) setViewing('idle');
    } catch {
      if (mounted.current) setViewing('unavailable');
    }
  };

  return (
    <Card variant="sunken" size="sm" className={styles.card} data-testid="chat-email-campaign-preview-card">
      <div className={styles.row}>
        <span className={styles.glyph} aria-hidden>
          <Mail size={18} />
        </span>
        <span className={styles.text}>
          <Typography variant="p3" color="secondary">
            {t('emailReview.card.title')}
          </Typography>
          {subject && (
            <Typography variant="p2" color="primary" className={styles.subject}>
              {subject}
            </Typography>
          )}
          {!reviewable && viewing === 'unavailable' && (
            <Typography variant="p3" color="secondary" role="alert">
              {t('emailReview.unavailable')}
            </Typography>
          )}
        </span>
      </div>
      {workspaceId && latest && <EmailCampaignInlinePreview workspaceId={workspaceId} card={card} cardKey={inlineKey} />}
      {reviewable && (
        <Button
          size="small"
          variant="outlined"
          disabled={!workspaceId || !canSend}
          onClick={() => {
            if (!workspaceId || !canSend) return;
            logPageClickBi({ page_name: 'chat', button_name: 'email_campaign_preview_open' }, { additionalFields: { review: reviewMode } });
            openEmailReview({ card, workspaceId, conversationId: conversationId ?? null, sendComments, beside });
          }}
          data-testid="emailCampaignPreviewOpen"
        >
          {t('emailReview.card.open')}
        </Button>
      )}
      {!reviewable && (
        <Button
          size="small"
          variant="outlined"
          disabled={!workspaceId}
          loading={viewing === 'loading'}
          onClick={() => void view()}
          data-testid="emailCampaignPreviewView"
        >
          {viewing === 'loading' ? t('emailReview.loading') : t('emailReview.card.view')}
        </Button>
      )}
    </Card>
  );
}
